/**
 * CoachRecommendationCard
 * Compact expert/coach card for AI chat recommendations.
 * Matches Figma node 5918:23497 -- 90px avatar, name+handle, followers+location,
 * Chat and Follow icon buttons. Card click navigates to profile.
 */

import { UserCheck, UserPlus } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { ChatBubbleIcon } from "@/components/icons"
import { formatFollowerCount } from "@/components/shared/formatFollowerCount"
import { useFollowActions } from "@/features/profile/hooks/useFollowActions"
import { batchFollowStatus } from "@/features/profile/services/follow.service"
import { useCreateDmConversation } from "../../hooks/useDmChat"
import type { CoachToolResult } from "../../types/ai-chat.types"

function getInitials(name: string): string {
  return (
    name
      .split(" ")
      .filter(word => word.length > 0)
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?"
  )
}

function formatLocation(city: string | null, state: string | null, country: string | null): string {
  return [city, state].filter(Boolean).join(", ") || country || ""
}

export function CoachRecommendationCard({ coaches }: { coaches: CoachToolResult[] }) {
  const navigate = useNavigate()
  const createDm = useCreateDmConversation()
  const { follow, unfollow } = useFollowActions()
  const [followState, setFollowState] = useState<Record<string, boolean>>({})

  // Fetch real follow status on mount (handles both old cached and new tool results)
  useEffect(() => {
    const ids = coaches.map(c => c.id)
    if (ids.length === 0) return
    batchFollowStatus(ids)
      .then(statuses => setFollowState(statuses))
      .catch(() => {})
  }, [coaches])

  if (coaches.length === 0) return null

  const isFollowing = (coach: CoachToolResult) => followState[coach.id] ?? false

  const handleFollow = (e: React.MouseEvent, coach: CoachToolResult) => {
    e.stopPropagation()
    const currently = isFollowing(coach)
    if (currently) {
      unfollow(coach.id)
    } else {
      follow(coach.id)
    }
    setFollowState(prev => ({ ...prev, [coach.id]: !currently }))
  }

  const handleChat = async (e: React.MouseEvent, coachId: string) => {
    e.stopPropagation()
    try {
      const conversation = await createDm.mutateAsync(coachId)
      navigate(`/chats/dm/${conversation.id}`)
    } catch {
      toast.error("Could not start conversation. Please try again.")
    }
  }

  return (
    <div className="flex flex-col gap-2.5">
      {coaches.map(coach => {
        const location = formatLocation(coach.city, coach.state, coach.country)

        return (
          <div
            key={coach.id}
            onClick={() => navigate(`/profile/${coach.id}`)}
            onKeyDown={e => {
              if (e.key === "Enter") navigate(`/profile/${coach.id}`)
            }}
            role="button"
            tabIndex={0}
            className="relative w-full h-[130px] rounded-[20px] border border-white/10 bg-white/5 hover:bg-white/[0.08] transition-colors cursor-pointer overflow-hidden"
          >
            {/* Avatar */}
            <div className="absolute left-[20px] top-[20px]">
              {coach.profilePictureUrl ? (
                <img
                  src={coach.profilePictureUrl}
                  alt={coach.name}
                  className="size-[90px] rounded-[12px] object-cover"
                />
              ) : (
                <div className="flex size-[90px] items-center justify-center rounded-[12px] bg-white/10 text-lg font-semibold text-white/50">
                  {getInitials(coach.name)}
                </div>
              )}
            </div>

            {/* Name + Handle */}
            <div className="absolute left-[130px] top-[41px] flex items-center gap-2.5">
              <span className="text-[18px] font-semibold text-text-primary opacity-90 leading-[22px]">
                {coach.name}
              </span>
            </div>

            {/* Followers + Location */}
            <div className="absolute left-[130px] top-[69px] flex items-center gap-2.5 text-[13px] text-text-primary/70 leading-[20px]">
              <span>{formatFollowerCount(0)} followers</span>
              {location && <span>{location}</span>}
            </div>

            {/* Action buttons */}
            <div className="absolute right-[19px] top-[19px] flex gap-1.5">
              <button
                type="button"
                onClick={e => handleChat(e, coach.id)}
                className="flex items-center justify-center size-[44px] rounded-[10px] bg-connect-glass-bg border border-connect-glass-border hover:bg-white/10 transition-colors"
                aria-label={`Chat with ${coach.name}`}
              >
                <ChatBubbleIcon className="size-[22px] text-text-primary/70" />
              </button>
              <button
                type="button"
                onClick={e => handleFollow(e, coach)}
                className="flex items-center justify-center size-[44px] rounded-[10px] bg-connect-glass-bg border border-connect-glass-border hover:bg-white/10 transition-colors"
                aria-label={isFollowing(coach) ? `Unfollow ${coach.name}` : `Follow ${coach.name}`}
              >
                {isFollowing(coach) ? (
                  <UserCheck className="size-[22px] text-brand-olive" />
                ) : (
                  <UserPlus className="size-[22px] text-text-primary/70" />
                )}
              </button>
            </div>
          </div>
        )
      })}
    </div>
  )
}
